import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EquipmentCatalogItem } from "../../screens/TicketingPortal/data/repairReplaceReturnEquipmentCatalog";
import type { TeamResponse } from "../../types/ticketingConfig.types";

export type TicketingTone = "neutral" | "positive" | "warning" | "critical";

/** Condition of a single serialised unit on a Repair / Replace / Return line. */
export type EquipmentUnitCondition = "failed" | "negligent";

/**
 * One equipment line as returned by the backend ticket detail API
 * inside `ticket.equipment.lines[]`.
 */
export interface TicketEquipmentLine {
  id: number;
  lineOrder: number;
  sku: string;
  serialNumber: string | null;
  serialUnreadable: boolean;
  quantityGood: number;
  quantityBroken: number;
  condition: string | null;
  damageNotes: string | null;
  failureNotes: string | null;
  wesContactTime: string | null;
  wesTimeZone: string | null;
  // New fields from enhanced API response
  type?: string | null;              // Equipment type (e.g., "Headset")
  generation?: number | null;        // Generation number (e.g., 1)
  dataCapture?: string | null;       // Data capture method (e.g., "SERIAL", "QTY")
  routesTo?: string | null;          // Where equipment ships to (e.g., "DEMATIC")
}

/**
 * Breakdown of equipment units by category
 */
export interface UnitsBreakdown {
  total: number;                 // Total units
  shipping: number;              // Units shipping to vendor
  eWaste: number;                // Units going to e-waste
}

/**
 * The `equipment` block returned by `GET /api/ticketing/tickets/{ticketNumber}`.
 * Populated only for Repair / Replace / Return category tickets.
 */
export interface TicketEquipmentData {
  equipmentPath: string;          // "RMA" | "NON_RMA" | "RECLAIM"
  vendor: string | null;
  conversionReference: string | null;
  siblingTicketIds: string[];
  lines: TicketEquipmentLine[];
  // New fields from enhanced API response
  replacementExpected?: string | null;    // e.g., "Yes, 1:1 on authorization"
  replacementRatio?: string | null;       // e.g., "1:1 on authorization"
  vendorDestination?: string | null;      // e.g., "DEMATIC CONTRACT REPAIR"
  unitsBreakdown?: UnitsBreakdown;        // Breakdown of units by category
}

/** One physical unit on a serial-captured line. */
export interface RepairReplaceReturnEquipmentUnit {
  id: string;
  serial: string;
  condition: EquipmentUnitCondition;
  serialUnreadable: boolean;
}

/** One equipment row on a Repair / Replace / Return ticket. */
export interface RepairReplaceReturnEquipmentLine {
  id: string;
  /** Catalog SKU; empty until the submitter picks an item. */
  sku: string;
  /** Used when the catalog item's capture mode is "serial". */
  units: RepairReplaceReturnEquipmentUnit[];
  /** Used when the catalog item's capture mode is "quantity". */
  quantityGood: string;
  quantityBad: string;
  damageNotes: string;
  failureNotes: string;
  /** WES devices only: best time to contact the site submitter, as a `YYYY-MM-DDTHH:mm` local value. */
  wesContactTime: string;
  /** WES devices only: time zone for `wesContactTime` (ET, CT, MT or PT). Defaults to CT. */
  wesContactTz: string;
}

export interface DashboardCounts {
  totalTicketCount: number;
  unassigned:      { count: number; metric: string };
  criticalOpen:    { count: number; metric: string };
  waitingOnUser:   { count: number; metric: string };
  closedThisWeek:  { count: number; metric: string };
}

export interface TicketingKpiItem {
  label: string;
  value: number;
  deltaText: string;
  tone?: TicketingTone;
}

export interface TicketingIncident {
  active: boolean;
  title: string;
  message: string;
  primaryActionLabel?: string;
  secondaryActionLabel?: string;
}

export interface WidespreadBanner {
  ticketId: string;
  title: string;
  message: string;
}

export type SubmitTicketLockedFields = {
  title?: boolean;
  category?: boolean;
  subCategory?: boolean;
  submitter?: boolean;
};

/** Raw shape returned by GET /ticketing/sustain-team/members */
export interface SustainTeamApiMember {
  id: number;
  userEmail: string;
  role: string;
  /** Reporting manager's email (optional). */
  managerEmail?: string | null;
  /** Sustain Team the member belongs to (TKT_Teams.id), read-only from this endpoint. */
  teamId?: number | null;
  shiftStartCst: string;
  shiftEndCst: string;
  channelInAppOnDuty: boolean;
  channelEmailOnDuty: boolean;
  channelTeamsOnDuty: boolean;
  channelInAppOffDuty: boolean;
  channelEmailOffDuty: boolean;
  channelTeamsOffDuty: boolean;
  triggerNewTicketOnDuty: boolean;
  triggerAssignedToMeOnDuty: boolean;
  triggerUnassignedFromMeOnDuty: boolean;
  triggerCommentAddedOnDuty: boolean;
  triggerAgingTicketOnDuty: boolean;
  triggerNewTicketOffDuty: boolean;
  triggerAssignedToMeOffDuty: boolean;
  triggerUnassignedFromMeOffDuty: boolean;
  triggerCommentAddedOffDuty: boolean;
  triggerAgingTicketOffDuty: boolean;
  notificationScope: string;
  customUrgencies: string[];
  workingStatus: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

/** In-app notification sent when a ticket the user is associated with transitions to "Waiting on User". */
export interface UserTicketNotif {
  id: string;
  ticketId: string;
  /**
   * For merge notifications only — the primary (surviving) ticket ID.
   * When set, the banner renders two clickable links: ticketId (absorbed)
   * and primaryTicketId (primary).
   */
  primaryTicketId?: string;
  /** Full human-readable message shown in the banner. */
  message: string;
  /** ISO-8601 timestamp when the notification was generated. */
  createdAt: string;
  read: boolean;
  /** Delivery channels — in-app is always rendered; email/teams are simulated for now. */
  channels: Array<"in-app" | "email" | "teams">;
}

/** Subset of PortalNotification stored in Redux for homepage display. */
export interface HomepageBannerNotif {
  id: string;
  type: string; // "incident" | "maint" | "general"
  title: string;
  message: string;
  linkedTicket?: string;
  includeActionButton?: boolean;
  /** ISO "YYYY-MM-DDTHH:mm" — banner auto-deactivates after this time. */
  endDate?: string;
  /** If true, banner displays until the linked ticket is closed, regardless of active status. */
  displayUntilClosed?: boolean;
  /** If false, banner is hidden (unless displayUntilClosed is true). */
  active?: boolean;
  /** If true, show banner to all users regardless of targeting. */
  allUsers?: boolean;
  /** Targeting criteria: if any field is defined and non-empty, user must match to see banner. */
  targetValues?: {
    regions?: string[];
    markets?: string[];
    locations?: string[];
    roles?: string[];
    systems?: string[];
  };
}

/** Cached item from GET /ticketing/notifications used by multiple screens. */
export interface TicketingNotificationApiItem {
  notificationId: number;
  type: string;
  title: string;
  message?: string;
  description?: string;
  relatedTicketId?: string;
  ticketNumber?: string;
  displayUntilClosed?: boolean;
  startDateTime?: string;
  endDateTime?: string;
  includeActionButton?: boolean;
  audienceTargetType?: string;
  targetValues?: {
    regions?: string[];
    markets?: string[];
    locations?: string[];
    roles?: string[];
    systems?: string[];
  };
  createdByUserId?: string;
  createdAt: string;
  active?: boolean;
  triggerTeamsNotification?: boolean;
  triggerBannerNotification?: boolean;
  triggerEmailNotification?: boolean;
  widespread?: boolean;
  notificationTriggered?: boolean;
  draft?: boolean;
  allUsers?: boolean;
  estimatedUserCount?: number;
}

export interface TicketingLandingAction {
  id: string;
  title: string;
  description: string;
  metric: string;
  metricLoading?: boolean;
  icon?: string;
  iconBackgroundColor?: string;
  iconColor?: string;
}

export interface TicketingLandingData {
  greeting: string;
  subtitle: string;
  incident?: TicketingIncident;
  kpis: TicketingKpiItem[];
  actions: TicketingLandingAction[];
}

export interface TicketingPortalData {
  submitTicket: {
    avgTime: string;
  };
  myTickets: {
    total: number;
    needsAction: number;
  };
  landing: TicketingLandingData;
}

export type TicketStatus =
  | "new"
  | "progress"
  | "waiting-it"
  | "waiting-tmv"
  | "submitted-ado"
  | "waiting-user"
  | "waiting-vendor"
  | "resolved"
  /** Ticket was absorbed into another ticket via the Merge workflow. Read-only. */
  | "closed-merged";

export type TicketUrgency = "critical" | "high" | "medium" | "low" | "informational";

/** Equipment ticket stage indicator — specific step within the status */
export type TicketStage =
  | "Awaiting return tracking"
  | "Return in transit"
  | "Awaiting vendor receipt"
  | "Awaiting inspection"
  | "Variance open"
  | "Awaiting replacement PO"
  | "Awaiting fulfillment review"
  | "Awaiting site accessory selection"
  | "Awaiting accessory validation"
  | "Awaiting vendor shipment"
  | "Replacement in transit"
  | "Awaiting site receipt"
  | "Reconciling"
  | "Complete";

/** Reason the user sees this ticket in My Tickets */
export type MyTicketReason = "SUBMITTED" | "STAKEHOLDER" | "VENDOR" | null;

export interface MergedTicketDetail {
  ticketNumber: string;
  title: string;
}

export interface MyTicketRow {
  id: string;
  title: string;
  description: string;
  category: string;
  subcategory?: string;
  location: string;
  sapId?: string;
  ticketLevel?: "Location" | "Market" | "Region";
  urgency: TicketUrgency;
  status: TicketStatus;
  /** ISO-8601 datetime string, e.g. "2026-04-18T10:22:00" */
  submitted: string;
  /** ISO-8601 datetime string, e.g. "2026-04-21T08:15:00" */
  lastUpdated: string;
  /** Email of the user who submitted the ticket */
  submittedBy?: string;
  /** Comma-separated emails listed as secondary stakeholders */
  stakeholders?: string;
  /** Emails of users attached to this ticket via merge */
  mergedUserEmails?: string[];
  /** ID of the primary ticket this ticket was absorbed into (status: closed-merged only) */
  mergedIntoTicketId?: string;
  /** IDs of tickets that were absorbed into this one via merge. */
  absorbedTicketIds?: string[];
  /** Details of tickets that were merged into this one (ticket number + title) */
  mergedTicketsDetails?: MergedTicketDetail[];
  /** Whether this ticket has duplicate tickets (for display in queue) */
  hasDuplicate?: boolean;
  /** True when the vendor declined at least one unit on inspection (RMA tickets) */
  hasDeclinedUnit?: boolean;
  /** Number of possible duplicates this ticket has */
  possibleDuplicateCount?: number;
  /** TKT_Teams.id the ticket is currently routed to (auto-routed or manually reassigned); null = unresolved */
  assignedTeamId?: number | null;
  /** Access request payload when the ticket is an access-request ticket */
  accessRequest?: AccessRequestDetail;
  /**
   * Status of the access-request approval workflow.
   * Only present when category === "Access Request".
   * Possible values: "Approved" | "Approved with Modification" | "Denied"
   */
  accessRequestStatus?: string | null;
  
  // ── Equipment-specific fields (null/undefined for non-equipment) ──
  /** Derived stage from shipment/review/fulfillment state */
  stage?: TicketStage | null;
  /** Destination vendor name (e.g., "Dematic Contract Repair") */
  vendor?: string | null;
  /** Total units on the ticket (serialized + quantity lines) */
  unitCount?: number | null;
  /** True when equipment path is BACKFILL or no units approved for return */
  noReturn?: boolean | null;
  /** Conversion reference for split tickets: "CONV-{location}-{sequence}" */
  conversionReference?: string | null;
  /** Why the signed-in user sees this ticket in My Tickets */
  myTicketReason?: MyTicketReason;
}

export interface StatusHistoryEntry {
  status: TicketStatus;
  enteredAt: number;   // epoch ms — when this status was entered
  exitedAt?: number;   // epoch ms — when this status was left (undefined = current)
}

export interface SustainQueueRow extends MyTicketRow {
  assignedTo: string;
  totalDuplicateCount?: number;
  assigneeInitials?: string;
  impactSites: number;
  aging: string;
  minutesAging?: number;
  incidentId?: string;
  adoId?: string;
  hasDuplicate?: boolean;
  subcategory?: string;
  market?: string;
  region?: string;
  submittedBy?: string;
  ticketLevel?: "Location" | "Market" | "Region";
  affectedSystem?: string;
  /** Numeric ID from backend — used for API calls like merge */
  backendId?: number;
  /** Raw numeric category ID from backend — used for PUT /ticketing/tickets/{id} */
  categoryNumId?: number;
  /** Raw numeric sub-category ID from backend — used for PUT /ticketing/tickets/{id} */
  subCategoryNumId?: number;
  /** Raw numeric tertiary category ID from backend */
  tertiaryId?: number | null;
  /** Resolved tertiary category name (display + client-side filtering) */
  tertiary?: string;
  /** Cumulative ms spent in "waiting-it" status (closed intervals only) */
  waitingItMs?: number;
  /** Deprecated numeric alias kept for backwards compat — in minutes */
  waitingItMinutes?: number;
  /** Cumulative ms spent in statuses OTHER than "waiting-it" (closed intervals only) */
  activeResolutionMs?: number;
  /** Epoch ms when the current status was entered */
  statusEnteredAt?: number;
  /** Full status transition history for this ticket */
  statusHistory?: StatusHistoryEntry[];
  /** ISO timestamp of when the ticket entered "Waiting on IT" status (from backend waitingOnItTimer field) */
  waitingOnItTimerISO?: string | null;
  /** Access request details present on access-request tickets (when available from API) */
  accessRequest?: AccessRequestDetail;
  
  // ── Equipment-specific fields ──
  /** Derived stage from shipment/review/fulfillment state */
  stage?: TicketStage | null;
  /** Total units on the ticket */
  unitCount?: number | null;
  /** Conversion reference for split tickets */
  conversionReference?: string | null;
  /** Why the user sees this ticket in My Tickets */
  myTicketReason?: MyTicketReason;
}

export interface ActivityItem {
  id: string;
  authorInitials: string;
  authorName: string;
  role: string;
  time: string;
  content: string;
  type: "user" | "system" | "sustain" | "servicenow";
  images?: string[]; // base64 data URIs stored alongside the comment
}

export interface NoteItem {
  id: string;
  authorInitials: string;
  authorName: string;
  role: string;
  time: string;
  body: string;
  avatarColor: string;
  images?: string[]; // base64 data URIs
}

export interface AffectedLocation {
  id: string;
  name: string;
  sapId: string;
  time: string;
  createdBy?: string;
  isSubmitter?: boolean;
}

export interface AccessRequestLocationDetail {
  locationType: "LOCATION" | "MARKET" | "REGION";
  locationId: string | null;
  locationName: string | null;
  region?: string | null;
  market?: string | null;
}

export interface AccessRequestDetail {
  requestedForEmail?: string;
  requestedForGpid?: string;
  requestedForName?: string;
  accessType: string;
  organization: string;
  department: string;
  role: string;
  currentRole?: string | null;
  justification?: string;
  endDate?: string;
  locations?: AccessRequestLocationDetail[];
}

export interface EditableTicketInfo {
  category: string;
  subcategory: string;
  /** Optional third-level tertiary category name; empty string when the sub-category has none selected. */
  tertiaryCategory?: string;
  ticketLevel: string;
  market: string;
  urgency: string;
  peopleImpacted: string;
  loadsImpacted: string;
  stakeholders: string;
  description: string;
  mmsToggle?: string;
}

export interface TicketDetailRecord extends MyTicketRow {
  submittedBy: string;
  /** Numeric DB id returned by backend detail API (distinct from ticket number). */
  backendTicketId?: number;
  categoryId?: number;
  subCategoryId?: number;
  /** Numeric tertiary category id, when the sub-category has tertiary options and one was selected. */
  tertiaryId?: number;
  /** Readable tertiary category name resolved from tertiaryId via categoryConfig. */
  tertiaryCategory?: string;
  market: string;
  subcategory: string;
  ticketLevel: "Location" | "Market" | "Region";
  peopleImpacted: string;
  /** Total number of people (pickers) impacted from API response */
  totalImpactedPickers?: number;
  /** ISO-8601 datetime when the ticket was resolved/closed. Used for the 7-day re-open window. */
  closedAt?: string;
  /** If this ticket was created by re-opening another ticket, stores the original ticket ID. */
  reopenedFromId?: string;
  /** Dynamic form values persisted with the ticket from backend detail API. */
  dynamicValues?: Array<{
    fieldConfigId: number;
    historicalLabel: string;
    value: string;
    fieldNoLongerActive: boolean;
  }>;
  loadsImpacted: string;
  stakeholders: string;
  incidentId?: string;
  resolutionPath?: string;
  assignedTo?: string;
  isWidespread?: boolean;
  adoId?: string;
  hasAdoAccess?: boolean; // If false, show plain text with tooltip
  affectedLocations: AffectedLocation[];
  activity: ActivityItem[];
  notes: NoteItem[];
  attachments?: AttachmentItem[];
  accessRequest?: AccessRequestDetail;
  /** Set after a merge to remind Sustain Team to review impact fields. */
  mergeImpactNote?: string;
  /** IDs of tickets that were absorbed into this one via merge. */
  absorbedTicketIds?: string[];
  /** If this ticket was absorbed into another, the numeric DB ID of the primary ticket. */
  mergedIntoTicketId?: string;
  /** Human-readable ticket number of the primary ticket this was merged into (e.g. "NTI-2026-00366"). */
  parentTicketRef?: string;
  /** Whether this ticket is related to Make, Move, Sell (MMS) toggle */
  mmsToggle?: boolean;
  /** Vendor this ticket routes to (e.g. "PEAK", "DEMATIC"). Present on RMA/equipment tickets. */
  vendor?: string;
  /**
   * Shared conversion reference when this ticket is part of a multi-vendor
   * Voice to Vision Reclaim Return split.  Format: CONV-[location]-[sequence].
   */
  conversionReference?: string | null;
  /** True when this ticket was created as one child of a multi-vendor conversion split. */
  isSplitChild?: boolean;
  /** Ticket numbers of the other tickets in the same conversion split. */
  siblingTicketIds?: string[];
  /** Full sibling ticket summaries for display in the conversion section. */
  siblingTickets?: Array<{
    ticketId: number;
    ticketNumber: string;
    vendor: string;
    equipmentCount: number;
    unitCount: number;
  }>;
  /**
   * Equipment block from the ticket detail API — present only on
   * Repair / Replace / Return tickets.
   */
  equipment?: TicketEquipmentData;
  /** RMA tickets: the vendor declined at least one unit on inspection */
  hasDeclinedUnit?: boolean;
  /** RMA tickets: negligent damage was declared at submission, so a decline is likely */
  negligentDamageNotice?: boolean;
  
  // ── Equipment stage indicator fields ──
  /** Derived stage from shipment/review/fulfillment state */
  stage?: TicketStage | null;
  /** Total units on the ticket */
  unitCount?: number | null;
  /** True when equipment path is BACKFILL or no units approved for return */
  noReturn?: boolean | null;
  /** Complete "Waiting on..." message with notification status */
  waitingOn?: string;
  /** Number of reminder notifications sent to vendor (0, 1, 2+) */
  reminderCount?: number;
}

export type TicketLevel = "Location" | "Market" | "Region" | "National" | "COE";
export type UrgencyLevel = "Critical" | "High" | "Medium" | "Low";
export type LoadImpact =
  | "0 - 25%"
  | "26 - 50%"
  | "51 - 75%"
  | "76 - 100%"
  | "None"
  | "Under 25%"
  | "25-50%"
  | "50-75%"
  | "75-100%"
  | "";

/**
 * Canonical resolution path options shared between Ticket Detail (triage)
 * and the analytics filter panel. This is the single source of truth.
 */
export const RESOLUTION_PATH_OPTIONS = [
  "Requires IT",
  "Internal Resolution",
] as const;

/**
 * Canonical ticket status options in display order.
 * Each entry has the Redux status key and the human-readable label shown in the UI.
 * Matches STATUS_TEXT in ticketStatusConstants and STATUS_OPTIONS in TicketDetailComponents.
 */
export const TICKET_STATUS_OPTIONS: Array<{ key: string; label: string }> = [
  { key: "new",           label: "New" },
  { key: "progress",      label: "In Progress" },
  { key: "waiting-it",    label: "Escalated to IT" },
  { key: "waiting-tmv",   label: "Waiting on Vendor" },
  { key: "waiting-vendor", label: "Waiting on Vendor" },
  { key: "submitted-ado", label: "Submitted to ADO" },
  { key: "waiting-user",  label: "Waiting on User" },
  { key: "resolved",      label: "Closed" },
  { key: "closed-merged", label: "Closed (Merged)" },
];

// GAP-11: Legacy fallback values only. Primary source of truth is /ticketing/form-config.
// UI should prefer explicit error handling when API config fails.
export const CATEGORY_OPTIONS = [
  "System Issue",
  "Data Issue",
  "Access Request",
  "Equipment Issue",
  "Process / How-To",
  "Reporting",
  "Enhancement Request",
  "System Enhancement",
  "Repair / Replace / Return",
] as const;

// GAP-11: Legacy fallback map only. Keep in sync only as a temporary safety net.
export const SUBCATEGORY_OPTIONS: Record<string, string[]> = {
  "System Issue": ["EWM", "Navigator", "SAP", "Voice Pick", "Vision Pick (WES)"],
  "Data Issue": ["Master Data", "Location Data", "Reporting Data"],
  "Access Request": ["New Access", "Modify Access", "Request Temporary Access"],
  "Equipment Issue": ["Scanner", "Printer", "Headset", "HUD"],
  "Process / How-To": ["Training Request", "Workflow Question"],
  Reporting: ["Enhancement", "Fix", "New"],
  "Enhancement Request": ["Navigator", "EWM", "SAP"],
  "System Enhancement": ["Centralized Purchasing", "EWM", "Navigator", "SAP"],
  "Repair / Replace / Return": [
    "RMA Repair or Replacement",
    "Non-RMA Replacement (End of Life)",
    "Voice to Vision Reclaim Return",
  ],
};

export const MARKET_OPTIONS = [
  "Southern California",
  "Northern California",
  "Pacific Northwest",
  "Southwest",
  "Midwest",
  "Northeast",
] as const;

// Category with no sub-categories/fields; only selectable by COE / Super User roles.
export const PROACTIVE_MONITORING_CATEGORY = "Proactive Monitoring";

export interface RelatedIssueItem {
  id: string;
  title: string;
  status: string;
  affectedLocations: number;
  locationsLabel?: string;
  urgency: UrgencyLevel;
  serviceRef?: string;
  alreadyAttached?: boolean; // Backend validation flag from duplicate check/search
}

export interface AttachmentItem {
  id: string | number;
  fileName: string;
  fileSize: string | number;
  fileSizeBytes?: number;
  storageUrl?: string;
}

export type SubmitTicketFieldErrorMap = Record<string, string>;

export interface SubmitTicketDraft {
  submitter: string; // GAP-01: rendered as visible TextInput, pre-populated from auth, editable in limited-access flow
  title: string;
  description: string;
  stakeholders: string;
  category: string;
  subCategory: string;
  tertiaryCategory: string;
  prefillCategoryId?: number;
  prefillSubCategoryId?: number;
  reopenedDynamicValues?: Array<{
    fieldConfigId: number;
    value: string;
  }>;
  level: TicketLevel;
  levelReference: string;
  sapId: string;
  urgency: UrgencyLevel;
  urgencyIsManual: boolean;
  impactedPeople: string;
  loadImpact: LoadImpact;
  attachments: AttachmentItem[];
  /** Set when this submission is a re-open of an existing closed ticket. */
  reopenedFromId?: string;
  /** "This is stopping my ability to Make, Move, Sell" toggle for System Issue category. */
  mmsToggle: boolean;
}

export interface SubmitTicketFormConfigPayload {
  categories: string[];
  subCategoryMap: Record<string, string[]>;
  categoryConfig: SubmitTicketCategoryConfig[];
  /** location name + role id the config was loaded for (beta category filtering) */
  contextKey: string;
  /** location + role the config was fetched for (beta rollout filter) */
  betaKey?: string;
}

export interface SubmitTicketTertiaryCategoryConfig {
  id: number;
  name: string;
  displayOrder: number;
  isActive: boolean;
}

export interface SubmitTicketSubCategoryConfig {
  id: number;
  name: string;
  displayOrder: number;
  isActive?: boolean;
  tertiaryCategoryCount?: number;
  tertiaryCategories?: SubmitTicketTertiaryCategoryConfig[];
}

export interface SubmitTicketCategoryConfig {
  id: number;
  name: string;
  displayOrder: number;
  isActive?: boolean;
  subcategoryCount?: number;
  subCategories: SubmitTicketSubCategoryConfig[];
}

export interface SubmitTicketFieldTrigger {
  categoryId: number | null;
  subCategoryId: number | null;
  tertiaryId: number | null;
  dependsOnFieldName?: string | null;
  dependsOnFieldValue?: string | null;
  dependsOnFieldValues?: string[] | null;
}

export interface SubmitTicketStaticOption {
  id: number;
  label: string;
  value: string;
  displayOrder: number;
}

export interface SubmitTicketDynamicField {
  id: number;
  fieldName: string;
  fieldLabel: string;
  fieldType: string;
  displayOrder: number;
  required: boolean;
  charLimit: number | null;
  dropdownSource: string | null;
  placeholder: string | null;
  tooltip: string | null;
  triggers: SubmitTicketFieldTrigger[];
  staticOptions: SubmitTicketStaticOption[];
}

interface TicketingPortalState {
  ticketingData: TicketingPortalData | null;
  myTickets: MyTicketRow[];
  myTicketsLoading: boolean;
  /** Error message from the last fetchMyTickets attempt; null when idle or succeeded. */
  myTicketsError: string | null;
  sustainQueue: SustainQueueRow[];
  sustainQueueLoading: boolean;
  /** Total ticket count from the last fetchSustainQueue API call. */
  sustainQueueTotalElements: number;
  /** Current page number for pagination. */
  sustainQueueCurrentPage: number;
  selectedTicketId: string | null;
  /** The screen that navigated to Ticket Detail — used for breadcrumb/back navigation. */
  ticketDetailSource: string;
  ticketDetails: Record<string, TicketDetailRecord>;
  detailLoading: boolean;
  /** When set, indicates that the currently-loading ticket (selectedTicketId) is an Access Request.
   *  Used to show AR skeleton UI while the detail API loads. Cleared when detail loads. */
  pendingAccessRequestTicketId: string | null;
  submitTicketQuery: string;
  submitTicketRelatedIssues: RelatedIssueItem[];
  submitTicketCategories: string[];
  submitTicketSubCategoryMap: Record<string, string[]>;
  submitTicketCategoryConfig: SubmitTicketCategoryConfig[];
  /** Location + role key that submitTicketCategoryConfig was fetched for */
  submitTicketCategoryContextKey: string | null;
  submitTicketCategoryConfigKey: string | null;
  submitTicketDynamicFields: SubmitTicketDynamicField[];
  submitTicketTriggerValues: Record<string, string>;
  submitTicketLockedFields: SubmitTicketLockedFields;
  submitTicketTriggerFieldOptions: Record<string, string[]>;
  submitTicketFormConfigLoading: boolean;
  submitTicketFormConfigError: string | null;
  submitTicketAttachedIssueIds: string[];
  submitTicketLoading: boolean;
  submitTicketError: string | null;
  submitTicketStatusMessage: string | null;
  submitTicketStatusTone: "info" | "success" | "error" | null;
  submitTicketDraft: SubmitTicketDraft;
  repairReplaceReturnEquipmentCatalog: EquipmentCatalogItem[];
  /** Every catalog device loaded so far, keyed by SKU, so lines added under a previous sub-category still resolve. */
  repairReplaceReturnEquipmentKnownItems: Record<string, EquipmentCatalogItem>;
  repairReplaceReturnEquipmentCatalogLoading: boolean;
  repairReplaceReturnEquipmentLines: RepairReplaceReturnEquipmentLine[];
  widespreadBanners: WidespreadBanner[];
  activeNotifCount: number;
  teamMemberCount: number;
  /** Shared cached payload from GET /ticketing/notifications for cross-screen reuse. */
  notificationsApiCache: TicketingNotificationApiItem[];
  /** True after the first successful notifications API fetch (even when empty). */
  notificationsApiFetched: boolean;
  /** Active homepage-banner notifications synced from NotificationsCommsScreen. */
  homepageBannerNotifs: HomepageBannerNotif[];
  /** IDs dismissed by the user for the current session. Resets on app restart. */
  dismissedHomepageBannerIds: string[];
  /** In-app per-ticket notifications for the logged-in user (e.g. "Waiting on User" transitions). */
  userTicketNotifs: UserTicketNotif[];
  /** Ticket IDs for which a "waiting-user" in-app notification has already been generated this session. */
  notifiedWaitingTicketIds: string[];
  /** Total elements count from the last fetchMyTickets API call — used by landing screen badge. */
  myTicketsTotalElements: number;
  /** Total closed tickets count from the API response — used for Closed pill status. */
  closedTicketsCount: number;
  /** Live KPI counts from GET /ticketing/dashboard/counts. */
  dashboardCounts: DashboardCounts | null;
  dashboardCountsLoading: boolean;
  /** Sustain team members from the /ticketing/sustain-team/members API. */
  sustainTeamMembers: SustainTeamApiMember[];
  sustainTeamMembersLoading: boolean;
  sustainTeamMembersError: string | null;
  sustainTeamMembersTotalCount: number;
  /** Complete team catalog from GET /ticketing/config/teams. */
  ticketingTeams: TeamResponse[];
  /** Teams returned by GET /ticketing/config/teams?excludeWithoutOpenTickets=true. */
  availableTicketingTeams: TeamResponse[];
/** All locations fetched from /api/common/all-locations */
  allLocations: LocationItem[];
  loading: boolean;
  error: string | null;
  /**
   * Snapshot of Sustain Queue list state saved just before navigating to
   * Ticket Detail so it can be fully restored when the user presses Back.
   * Cleared when navigating to the Sustain Queue screen fresh (not from Back).
   */
  sustainQueueListState: SustainQueueListState | null;
  /**
   * Snapshot of My Tickets list state saved just before navigating to
   * Ticket Detail so it can be fully restored when the user presses Back.
   * Cleared when navigating to My Tickets fresh (not from Back).
   */
  myTicketsListState: MyTicketsListState | null;
}

/**
 * Serializable snapshot of Sustain Queue list state saved before navigating
 * to Ticket Detail so filters, page, and view state can be restored on Back.
 */
export interface SustainQueueListState {
  query: string;
  activeStatus: "all" | TicketStatus;
  viewMode: "kanban" | "list";
  kanbanFilters: {
    statuses: TicketStatus[];
    urgencies: string[];
    categories: string[];
    subcategories: string[];
    locations: string[];
    markets: string[];
    regions: string[];
    assignees: string[];
    /** Optional for snapshots persisted before these filters were added. */
    tertiaries?: string[];
    managers?: string[];
  };
  currentPage: number;
  /** ID of the ticket the user opened — used to scroll back to that row on return. */
  lastOpenedTicketId?: string | null;
}

/**
 * Serializable snapshot of My Tickets list state saved before navigating
 * to Ticket Detail so filters, sort, page, and search can be restored on Back.
 */
export interface MyTicketsListState {
  query: string;
  activeStatus: "all" | TicketStatus;
  sortColumn: string;
  sortDirection: "asc" | "desc";
  webFilters: {
    urgency: string[];
    status: string[];
    category: string[];
    subcategory: string[];
    location: string[];
    title: string[];
  };
  currentPage: number;
  /** ID of the ticket the user opened — used to scroll back to that row on return. */
  lastOpenedTicketId?: string | null;
}

/** Shape of a single record from /api/common/all-locations */
export interface LocationItem {
  id: number;
  locId: string;
  locSapId: string;
  locName: string;
  region: string;
  market: string;
  status: string;
}

const createInitialSubmitTicketDraft = (): SubmitTicketDraft => ({
  submitter: "", // seeded with authUser.email in SubmitTicketScreen on mount (GAP-01)
  title: "",
  description: "",
  stakeholders: "",
  category: "",
  subCategory: "",
  tertiaryCategory: "",
  level: "Location",
  levelReference: "",
  sapId: "",
  urgency: "High",
  urgencyIsManual: false,
  impactedPeople: "",
  loadImpact: "",
  attachments: [],
  mmsToggle: false,
});

const getUrgencyRank = (urgency: UrgencyLevel): number => {
  switch (urgency) {
    case "Low":
      return 1;
    case "Medium":
      return 2;
    case "High":
      return 3;
    case "Critical":
      return 4;
    default:
      return 1;
  }
};

const calculateAutoUrgency = (
  impactedPeople: string,
  loadImpact: LoadImpact,
  fallback: UrgencyLevel
): UrgencyLevel => {
  const normalizedPeople = impactedPeople.trim().toLowerCase();
  const normalizedLoad = loadImpact.trim().toLowerCase();

  let peopleUrgency: UrgencyLevel | null = null;
  if (["just me"].includes(normalizedPeople)) peopleUrgency = "Low";
  else if (["2-5", "6-25"].includes(normalizedPeople)) peopleUrgency = "Medium";
  else if (["25-50", "50+", "entire location"].includes(normalizedPeople)) peopleUrgency = "High";

  let loadUrgency: UrgencyLevel | null = null;
  if (["none"].includes(normalizedLoad)) loadUrgency = "Low";
  else if (["under 25%", "0 - 25%", "25-50%", "26 - 50%"].includes(normalizedLoad)) loadUrgency = "Medium";
  else if (["50-75%", "51 - 75%", "75-100%", "76 - 100%"].includes(normalizedLoad)) loadUrgency = "High";

  if (!peopleUrgency && !loadUrgency) {
    return fallback;
  }

  if (!peopleUrgency) {
    return loadUrgency as UrgencyLevel;
  }
  if (!loadUrgency) {
    return peopleUrgency;
  }

  return getUrgencyRank(peopleUrgency) >= getUrgencyRank(loadUrgency)
    ? peopleUrgency
    : (loadUrgency as UrgencyLevel);
};

const initialState: TicketingPortalState = {
  ticketingData: null,
  myTickets: [],
  myTicketsLoading: false,
  myTicketsError: null,
  sustainQueue: [],
  sustainQueueLoading: false,
  sustainQueueTotalElements: 0,
  sustainQueueCurrentPage: 1,
  selectedTicketId: null,
  ticketDetailSource: "My Tickets",
  ticketDetails: {},
  detailLoading: false,
  pendingAccessRequestTicketId: null,
  submitTicketQuery: "",
  submitTicketRelatedIssues: [],
  submitTicketCategories: [...CATEGORY_OPTIONS],
  submitTicketSubCategoryMap: { ...SUBCATEGORY_OPTIONS },
  submitTicketCategoryConfig: [],
  submitTicketCategoryContextKey: null,
  submitTicketCategoryConfigKey: null,
  submitTicketDynamicFields: [],
  submitTicketTriggerValues: {},
  submitTicketTriggerFieldOptions: {},
  submitTicketFormConfigLoading: false,
  submitTicketFormConfigError: null,
  submitTicketAttachedIssueIds: [], // GAP-09: cleared mock seed "NTI-2026-00418"
  submitTicketLoading: false,
  submitTicketError: null,
  submitTicketStatusMessage: null,
  submitTicketStatusTone: null,
  submitTicketDraft: createInitialSubmitTicketDraft(),
  repairReplaceReturnEquipmentCatalog: [],
  repairReplaceReturnEquipmentKnownItems: {},
  repairReplaceReturnEquipmentCatalogLoading: false,
  repairReplaceReturnEquipmentLines: [],
  submitTicketLockedFields: {},
  widespreadBanners: [],
  activeNotifCount: 0,
  teamMemberCount: 0,
  notificationsApiCache: [],
  notificationsApiFetched: false,
  // Start with empty homepage banners - populated by API calls instead of mock data
  homepageBannerNotifs: [],
  dismissedHomepageBannerIds: [],
  userTicketNotifs: [],
  notifiedWaitingTicketIds: [],
  myTicketsTotalElements: 0,
  closedTicketsCount: 0,
  dashboardCounts: null,
  dashboardCountsLoading: false,
  sustainTeamMembers: [],
  sustainTeamMembersLoading: false,
  sustainTeamMembersError: null,
  sustainTeamMembersTotalCount: 0,
  ticketingTeams: [],
  availableTicketingTeams: [],
  allLocations: [],
  loading: false,
  error: null,
  sustainQueueListState: null,
  myTicketsListState: null,
};

const ticketingPortalSlice = createSlice({
  name: "ticketingPortal",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean | undefined>) => {
      state.loading = action.payload ?? true;
      if (state.loading) {
        state.error = null;
      }
    },
    setData: (state, action: PayloadAction<TicketingPortalData>) => {
      state.ticketingData = action.payload;
      state.loading = false;
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
      state.ticketingData = null;
    },
    setMyTicketsLoading: (state, action: PayloadAction<boolean | undefined>) => {
      state.myTicketsLoading = action.payload ?? true;
      if (state.myTicketsLoading) {
        state.error = null;
        state.myTicketsError = null;
      }
    },
    setMyTickets: (state, action: PayloadAction<MyTicketRow[]>) => {
      state.myTickets = action.payload;
      state.myTicketsLoading = false;
      state.myTicketsError = null;
      state.error = null;
    },
    setMyTicketsTotalElements: (state, action: PayloadAction<number>) => {
      state.myTicketsTotalElements = action.payload;
    },
    setClosedTicketsCount: (state, action: PayloadAction<number>) => {
      state.closedTicketsCount = action.payload;
    },
    /** Sets a My Tickets-specific error and stops the loading spinner. */
    setMyTicketsError: (state, action: PayloadAction<string>) => {
      state.myTicketsError = action.payload;
      state.myTicketsLoading = false;
    },
    setSustainQueueLoading: (state, action: PayloadAction<boolean | undefined>) => {
      state.sustainQueueLoading = action.payload ?? true;
      if (state.sustainQueueLoading) {
        state.error = null;
      }
    },
    setSustainQueue: (state, action: PayloadAction<SustainQueueRow[]>) => {
      state.sustainQueue = action.payload;
      state.sustainQueueLoading = false;
      state.error = null;
    },
    setSustainQueueTotalElements: (state, action: PayloadAction<number>) => {
      state.sustainQueueTotalElements = action.payload;
    },
    setSustainQueueCurrentPage: (state, action: PayloadAction<number>) => {
      state.sustainQueueCurrentPage = action.payload;
    },
    setSelectedTicketId: (state, action: PayloadAction<string | null>) => {
      state.selectedTicketId = action.payload;
    },
    setTicketDetailSource: (state, action: PayloadAction<string>) => {
      state.ticketDetailSource = action.payload;
    },
    setDetailLoading: (state, action: PayloadAction<boolean | undefined>) => {
      state.detailLoading = action.payload ?? true;
      if (state.detailLoading) {
        state.error = null;
      } else {
        // Clear pending AR flag when detail finishes loading
        state.pendingAccessRequestTicketId = null;
      }
    },
    setPendingAccessRequestTicketId: (state, action: PayloadAction<string | null>) => {
      state.pendingAccessRequestTicketId = action.payload;
    },
    upsertTicketDetail: (state, action: PayloadAction<TicketDetailRecord>) => {
      const existing = state.ticketDetails[action.payload.id];
      // Preserve notes if they were already fetched (don't overwrite with empty array)
      const preservedNotes = (existing?.notes && existing.notes.length > 0) ? existing.notes : action.payload.notes;
      
      // Preserve SAPID in affected locations when merging with new API data
      let preservedAffectedLocations = action.payload.affectedLocations;
      if (existing?.affectedLocations && existing.affectedLocations.length > 0) {
        // Create a map of location name (lowercase) to SAPID from existing state
        const existingSapIdMap = new Map<string, string>();
        existing.affectedLocations.forEach((loc) => {
          if (loc.sapId) {
            existingSapIdMap.set(loc.name.toLowerCase(), loc.sapId);
          }
        });

        // Merge SAPID from existing state into new affected locations if missing
        preservedAffectedLocations = preservedAffectedLocations.map((loc) => {
          const existingSapId = existingSapIdMap.get(loc.name.toLowerCase());
          return {
            ...loc,
            sapId: loc.sapId || existingSapId || "",
          };
        });
      }
      
      state.ticketDetails[action.payload.id] = {
        ...action.payload,
        notes: preservedNotes,
        affectedLocations: preservedAffectedLocations,
      };
      state.detailLoading = false;
      state.error = null;
      // Clear pending AR flag when detail successfully loads
      state.pendingAccessRequestTicketId = null;
      
      console.log(`[upsertTicketDetail] Ticket ${action.payload.id} updated. Notes count: ${preservedNotes.length}`);
    },
    updateTicketDetailInfo: (
      state,
      action: PayloadAction<{ ticketId: string; updates: Partial<EditableTicketInfo> }>
    ) => {
      const ticket = state.ticketDetails[action.payload.ticketId];
      if (!ticket) {
        return;
      }

      const { updates } = action.payload;
      if (updates.category !== undefined) ticket.category = updates.category;
      if (updates.subcategory !== undefined) ticket.subcategory = updates.subcategory;
      if (updates.tertiaryCategory !== undefined) ticket.tertiaryCategory = updates.tertiaryCategory;
      if (updates.ticketLevel !== undefined) ticket.ticketLevel = updates.ticketLevel as "Location" | "Market" | "Region";
      if (updates.market !== undefined) ticket.market = updates.market;
      if (updates.urgency !== undefined) ticket.urgency = updates.urgency as TicketUrgency;
      if (updates.peopleImpacted !== undefined) ticket.peopleImpacted = updates.peopleImpacted;
      if (updates.loadsImpacted !== undefined) ticket.loadsImpacted = updates.loadsImpacted;
      if (updates.stakeholders !== undefined) ticket.stakeholders = updates.stakeholders;
      if (updates.description !== undefined) ticket.description = updates.description;

      // Persist dynamic field values when included with edit (cast to any since EditableTicketInfo doesn't define them)
      const anyUpdates = updates as any;
      if (anyUpdates.dynamicValues !== undefined) {
        ticket.dynamicValues = anyUpdates.dynamicValues;
      }
      // Persist categoryId / subCategoryId when resolved by the save thunk
      if (anyUpdates.categoryId !== undefined) ticket.categoryId = anyUpdates.categoryId;
      if (anyUpdates.subCategoryId !== undefined) ticket.subCategoryId = anyUpdates.subCategoryId;
      if (anyUpdates.tertiaryId !== undefined) ticket.tertiaryId = anyUpdates.tertiaryId;

      ticket.lastUpdated = "Just now";
      const listIndex = state.myTickets.findIndex((row) => row.id === ticket.id);
      if (listIndex >= 0) {
        state.myTickets[listIndex] = {
          ...state.myTickets[listIndex],
          title: ticket.title,
          description: ticket.description,
          category: ticket.category,
          location: ticket.location,
          urgency: ticket.urgency,
          status: ticket.status,
          lastUpdated: ticket.lastUpdated,
        };
      }
    },
    addTicketActivityComment: (
      state,
      action: PayloadAction<{ ticketId: string; activity: ActivityItem }>
    ) => {
      const ticket = state.ticketDetails[action.payload.ticketId];
      if (!ticket) {
        return;
      }
      ticket.activity = [...(ticket.activity || []), action.payload.activity];
      ticket.lastUpdated = "Just now";
    },
    addTicketNote: (state, action: PayloadAction<{ ticketId: string; note: NoteItem }>) => {
      const ticket = state.ticketDetails[action.payload.ticketId];
      if (!ticket) {
        return;
      }
      ticket.notes = [action.payload.note, ...(ticket.notes || [])];
      ticket.lastUpdated = "Just now";
    },
    setTicketNotes: (state, action: PayloadAction<{ ticketId: string; notes: NoteItem[] }>) => {
      const ticket = state.ticketDetails[action.payload.ticketId];
      if (!ticket) {
        console.warn(`[setTicketNotes] Ticket ${action.payload.ticketId} not found in store, notes will be lost. Notes count: ${action.payload.notes.length}`);
        return;
      }
      console.log(`[setTicketNotes] Setting ${action.payload.notes.length} notes for ticket ${action.payload.ticketId}`);
      ticket.notes = action.payload.notes;
    },
    addAffectedLocation: (
      state,
      action: PayloadAction<{ ticketId: string; location: AffectedLocation }>
    ) => {
      const ticket = state.ticketDetails[action.payload.ticketId];
      if (!ticket) {
        return;
      }

      const exists = ticket.affectedLocations.some(
        (loc) => loc.name.toLowerCase() === action.payload.location.name.toLowerCase()
      );
      if (exists) {
        return;
      }

      ticket.affectedLocations = [action.payload.location, ...ticket.affectedLocations];
      ticket.lastUpdated = "Just now";
    },
    removeAffectedLocation: (
      state,
      action: PayloadAction<{ ticketId: string; locationId: string }>
    ) => {
      const ticket = state.ticketDetails[action.payload.ticketId];
      if (!ticket) {
        return;
      }

      ticket.affectedLocations = ticket.affectedLocations.filter(
        (loc) => !(loc.id === action.payload.locationId && !loc.isSubmitter)
      );
      ticket.lastUpdated = "Just now";
    },
    addTicketStakeholderEmail: (
      state,
      action: PayloadAction<{ ticketId: string; email: string }>
    ) => {
      const ticket = state.ticketDetails[action.payload.ticketId];
      if (!ticket) {
        return;
      }
      const normalizedEmail = action.payload.email.trim();
      if (!normalizedEmail) {
        return;
      }
      const existing = (ticket.stakeholders ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (existing.some((e) => e.toLowerCase() === normalizedEmail.toLowerCase())) {
        return;
      }
      ticket.stakeholders = [...existing, normalizedEmail].join(", ");
      ticket.lastUpdated = "Just now";
    },
    updateAffectedLocations: (
      state,
      action: PayloadAction<{
        ticketId: string;
        locations: Array<{
          id: number;
          locName: string;
          locId: string;
          sapId?: string | null;
          reportedTime?: string | null;
          isSubmitter: boolean;
        }>;
      }>
    ) => {
      const ticket = state.ticketDetails[action.payload.ticketId];
      if (!ticket) {
        return;
      }

      // Map the API response locations to AffectedLocation objects with sapId preserved
      const updatedLocations = action.payload.locations.map((location, index) => ({
        id: `${location.locId || location.locName || "loc"}-${index}`,
        name: location.locName,
        sapId: location.sapId || "",
        time: location.reportedTime || "",
        createdBy: undefined,
        isSubmitter: Boolean(location.isSubmitter),
      }));

      ticket.affectedLocations = updatedLocations;
    },
    updateTicketStatus: (
      state,
      action: PayloadAction<{ ticketId: string; status: TicketStatus }>
    ) => {
      const ticket = state.ticketDetails[action.payload.ticketId];
      if (!ticket) return;
      ticket.status = action.payload.status;
      ticket.lastUpdated = "Just now";
      const listIndex = state.myTickets.findIndex((row) => row.id === ticket.id);
      if (listIndex >= 0) {
        state.myTickets[listIndex] = { ...state.myTickets[listIndex], status: action.payload.status, lastUpdated: "Just now" };
      }
      // Auto-remove widespread banner when ticket is resolved or closed
      if (action.payload.status === "resolved") {
        state.widespreadBanners = state.widespreadBanners.filter((b) => b.ticketId !== action.payload.ticketId);
        if (state.ticketDetails[action.payload.ticketId]) {
          state.ticketDetails[action.payload.ticketId].isWidespread = false;
        }
      }
    },
    setTriageInfo: (
      state,
      action: PayloadAction<{ ticketId: string; resolutionPath?: string; assignedTo?: string; isWidespread?: boolean; adoId?: string }>
    ) => {
      const ticket = state.ticketDetails[action.payload.ticketId];
      if (!ticket) return;
      if (action.payload.resolutionPath !== undefined) ticket.resolutionPath = action.payload.resolutionPath;
      if (action.payload.assignedTo !== undefined) ticket.assignedTo = action.payload.assignedTo;
      if (action.payload.isWidespread !== undefined) {
        ticket.isWidespread = action.payload.isWidespread;
        if (action.payload.isWidespread) {
          // Create automatic widespread banner
          const exists = state.widespreadBanners.some((b) => b.ticketId === ticket.id);
          if (!exists) {
            state.widespreadBanners.push({
              ticketId: ticket.id,
              title: ticket.title,
              message: `Widespread issue reported. Ticket ID: ${ticket.id}. Sustain Team actively working on resolution.`,
            });
          }
        } else {
          // Remove banner when flag is cleared
          state.widespreadBanners = state.widespreadBanners.filter((b) => b.ticketId !== ticket.id);
        }
      }
      if (action.payload.isWidespread !== undefined) ticket.isWidespread = action.payload.isWidespread;
      if (action.payload.adoId !== undefined) ticket.adoId = action.payload.adoId;
    },
    setSubmitTicketQuery: (state, action: PayloadAction<string>) => {
      state.submitTicketQuery = action.payload;
    },
    setSubmitTicketFormConfigLoading: (state, action: PayloadAction<boolean | undefined>) => {
      state.submitTicketFormConfigLoading = action.payload ?? true;
      if (state.submitTicketFormConfigLoading) {
        state.submitTicketFormConfigError = null;
      }
    },
    setSubmitTicketFormConfigSuccess: (state, action: PayloadAction<SubmitTicketFormConfigPayload>) => {
      state.submitTicketCategories = action.payload.categories;
      state.submitTicketSubCategoryMap = action.payload.subCategoryMap;
      state.submitTicketCategoryConfig = action.payload.categoryConfig;
      state.submitTicketCategoryContextKey = action.payload.contextKey;
      state.submitTicketFormConfigLoading = false;
      state.submitTicketFormConfigError = null;

      // Re-open redirect path: if IDs are present, resolve names from fresh config.
      if (state.submitTicketDraft.prefillCategoryId != null) {
        const categoryMatch = action.payload.categoryConfig.find(
          (cat) => cat.id === state.submitTicketDraft.prefillCategoryId
        );
        if (categoryMatch) {
          state.submitTicketDraft.category = categoryMatch.name;
          if (state.submitTicketDraft.prefillSubCategoryId != null) {
            const subMatch = categoryMatch.subCategories.find(
              (sub) => sub.id === state.submitTicketDraft.prefillSubCategoryId
            );
            state.submitTicketDraft.subCategory = subMatch?.name ?? "";
          }
        }
      }

      const currentCategory = state.submitTicketDraft.category;
      const currentSubCategory = state.submitTicketDraft.subCategory;
      const currentTertiaryCategory = state.submitTicketDraft.tertiaryCategory;
      const categories = action.payload.categories;
      const subCategoryMap = action.payload.subCategoryMap;

      if (currentCategory && !categories.includes(currentCategory)) {
        state.submitTicketDraft.category = "";
        state.submitTicketDraft.subCategory = "";
        state.submitTicketDraft.tertiaryCategory = "";
        state.submitTicketTriggerValues = {};
        return;
      }

      const validSubCategories = subCategoryMap[currentCategory] ?? [];
      if (currentSubCategory && !validSubCategories.includes(currentSubCategory)) {
        state.submitTicketDraft.subCategory = "";
        state.submitTicketDraft.tertiaryCategory = "";
        state.submitTicketTriggerValues = {};
        return;
      }

      const categoryMatch = action.payload.categoryConfig.find((cat) => cat.name === currentCategory);
      const subCategoryMatch = categoryMatch?.subCategories.find((sub) => sub.name === currentSubCategory);
      const validTertiaryCategories = (subCategoryMatch?.tertiaryCategories ?? []).map((t) => t.name);
      if (currentTertiaryCategory && !validTertiaryCategories.includes(currentTertiaryCategory)) {
        state.submitTicketDraft.tertiaryCategory = "";
        state.submitTicketTriggerValues = {};
      }
    },
    setSubmitTicketDynamicFields: (state, action: PayloadAction<SubmitTicketDynamicField[]>) => {
      state.submitTicketDynamicFields = action.payload;

      // Re-open redirect path: restore trigger values from saved fieldConfigId/value pairs.
      const reopenedDynamicValues = state.submitTicketDraft.reopenedDynamicValues ?? [];
      if (!reopenedDynamicValues.length) {
        return;
      }

      reopenedDynamicValues.forEach((item) => {
        const field = action.payload.find((f) => f.id === item.fieldConfigId);
        if (!field) {
          return;
        }

        // Don't overwrite user edits if they already changed a field in-session.
        if (state.submitTicketTriggerValues[field.fieldName] == null) {
          state.submitTicketTriggerValues[field.fieldName] = item.value;
        }
      });
    },
    setSubmitTicketTriggerFieldOptions: (
      state,
      action: PayloadAction<{ fieldName: string; options: string[] }>
    ) => {
      state.submitTicketTriggerFieldOptions[action.payload.fieldName] = action.payload.options;
    },
    setSubmitTicketTriggerFieldValue: (
      state,
      action: PayloadAction<{ fieldName: string; value: string }>
    ) => {
      state.submitTicketTriggerValues[action.payload.fieldName] = action.payload.value;
    },
    clearSubmitTicketTriggerValues: (state) => {
      state.submitTicketTriggerValues = {};
    },
    setSubmitTicketFormConfigError: (state, action: PayloadAction<string>) => {
      state.submitTicketFormConfigError = action.payload;
      state.submitTicketFormConfigLoading = false;
    },
    setSubmitTicketLoading: (state, action: PayloadAction<boolean | undefined>) => {
      state.submitTicketLoading = action.payload ?? true;
      if (state.submitTicketLoading) {
        state.submitTicketError = null;
      }
    },
    setSubmitTicketRelatedIssues: (state, action: PayloadAction<RelatedIssueItem[]>) => {
      state.submitTicketRelatedIssues = action.payload;
      state.submitTicketLoading = false;
      state.submitTicketError = null;
    },
    setSubmitTicketError: (state, action: PayloadAction<string>) => {
      state.submitTicketError = action.payload;
      state.submitTicketLoading = false;
    },
    setSubmitTicketSubmitter: (state, action: PayloadAction<string>) => {
      state.submitTicketDraft.submitter = action.payload;
    },
    setSubmitTicketTitle: (state, action: PayloadAction<string>) => {
      state.submitTicketDraft.title = action.payload;
    },
    setSubmitTicketDescription: (state, action: PayloadAction<string>) => {
      state.submitTicketDraft.description = action.payload;
    },
    setSubmitTicketStakeholders: (state, action: PayloadAction<string>) => {
      state.submitTicketDraft.stakeholders = action.payload;
    },
    setSubmitTicketCategory: (state, action: PayloadAction<string>) => {
      state.submitTicketDraft.category = action.payload;
      state.submitTicketDraft.subCategory = "";
      state.submitTicketDraft.tertiaryCategory = "";
      state.submitTicketDraft.mmsToggle = false;
      state.submitTicketTriggerValues = {};
      state.repairReplaceReturnEquipmentLines = [];
      // Recalculate urgency without MMS boost since toggle is reset to false
      if (!state.submitTicketDraft.urgencyIsManual) {
        state.submitTicketDraft.urgency = calculateAutoUrgency(
          state.submitTicketDraft.impactedPeople,
          state.submitTicketDraft.loadImpact,
          state.submitTicketDraft.urgency
        );
      }
    },
    setSubmitTicketSubCategory: (state, action: PayloadAction<string>) => {
      state.submitTicketDraft.subCategory = action.payload;
      state.submitTicketDraft.tertiaryCategory = "";
      state.submitTicketTriggerValues = {};
    },
    setSubmitTicketTertiaryCategory: (state, action: PayloadAction<string>) => {
      state.submitTicketDraft.tertiaryCategory = action.payload;
      state.submitTicketTriggerValues = {};
    },
    setSubmitTicketLevel: (state, action: PayloadAction<TicketLevel>) => {
      state.submitTicketDraft.level = action.payload;
      state.submitTicketDraft.levelReference = "";
      state.submitTicketDraft.sapId = "";
    },
    setSubmitTicketLevelReference: (state, action: PayloadAction<string>) => {
      state.submitTicketDraft.levelReference = action.payload;
    },
    setSubmitTicketSapId: (state, action: PayloadAction<string>) => {
      state.submitTicketDraft.sapId = action.payload;
    },
    setSubmitTicketUrgency: (state, action: PayloadAction<UrgencyLevel>) => {
      state.submitTicketDraft.urgency = action.payload;
      state.submitTicketDraft.urgencyIsManual = true;
    },
    setSubmitTicketImpactedPeople: (state, action: PayloadAction<string>) => {
      state.submitTicketDraft.impactedPeople = action.payload;
      if (!state.submitTicketDraft.urgencyIsManual) {
        state.submitTicketDraft.urgency = calculateAutoUrgency(
          state.submitTicketDraft.impactedPeople,
          state.submitTicketDraft.loadImpact,
          state.submitTicketDraft.urgency
        );
      }
    },
    setSubmitTicketLoadImpact: (state, action: PayloadAction<LoadImpact>) => {
      state.submitTicketDraft.loadImpact = action.payload;
      if (!state.submitTicketDraft.urgencyIsManual) {
        state.submitTicketDraft.urgency = calculateAutoUrgency(
          state.submitTicketDraft.impactedPeople,
          state.submitTicketDraft.loadImpact,
          state.submitTicketDraft.urgency
        );
      }
    },
    attachSubmitTicketToIssue: (state, action: PayloadAction<string>) => {
      if (!state.submitTicketAttachedIssueIds.includes(action.payload)) {
        state.submitTicketAttachedIssueIds.push(action.payload);
      }
      state.submitTicketStatusMessage = `Your location has been attached to ${action.payload}`;
      state.submitTicketStatusTone = "success";
    },
    addSubmitTicketAttachment: (state, action: PayloadAction<AttachmentItem>) => {
      if (state.submitTicketDraft.attachments.length >= 5) {
        state.submitTicketStatusMessage = "Maximum 5 files supported.";
        state.submitTicketStatusTone = "error";
        return;
      }
      state.submitTicketDraft.attachments.push(action.payload);
    },
    removeSubmitTicketAttachment: (state, action: PayloadAction<string>) => {
      state.submitTicketDraft.attachments = state.submitTicketDraft.attachments.filter(
        (item) => item.id !== action.payload
      );
    },
    setSubmitTicketMmsToggle: (state, action: PayloadAction<boolean>) => {
      state.submitTicketDraft.mmsToggle = action.payload;
      
      if (action.payload === true) {
        // When enabling toggle: ALWAYS set to Critical, overriding any manual urgency selection
        state.submitTicketDraft.urgency = "Critical";
      } else {
        // When disabling toggle: ALWAYS recalculate from impact values
        // The toggle was forcing Critical, so removing it releases that override
        state.submitTicketDraft.urgency = calculateAutoUrgency(
          state.submitTicketDraft.impactedPeople,
          state.submitTicketDraft.loadImpact,
          state.submitTicketDraft.urgency
        );
        // Also reset manual flag so impact changes can recalculate automatically again
        state.submitTicketDraft.urgencyIsManual = false;
      }
    },
    setSubmitTicketStatusMessage: (
      state,
      action: PayloadAction<{ message: string | null; tone: "info" | "success" | "error" | null }>
    ) => {
      state.submitTicketStatusMessage = action.payload.message;
      state.submitTicketStatusTone = action.payload.tone;
    },
    setDashboardCountsLoading: (state, action: PayloadAction<boolean>) => {
      state.dashboardCountsLoading = action.payload;
    },
    setDashboardCounts: (state, action: PayloadAction<DashboardCounts>) => {
      state.dashboardCounts = action.payload;
      state.dashboardCountsLoading = false;
    },
    setActiveNotifCount: (state, action: PayloadAction<number>) => {
      state.activeNotifCount = action.payload;
    },
    setTeamMemberCount: (state, action: PayloadAction<number>) => {
      state.teamMemberCount = action.payload;
    },
    setTicketingTeams: (state, action: PayloadAction<TeamResponse[]>) => {
      state.ticketingTeams = action.payload;
    },
    setAvailableTicketingTeams: (state, action: PayloadAction<TeamResponse[]>) => {
      state.availableTicketingTeams = action.payload;
    },
    setNotificationsApiCache: (
      state,
      action: PayloadAction<{ content: TicketingNotificationApiItem[]; activeCount: number }>
    ) => {
      state.notificationsApiCache = action.payload.content;
      state.notificationsApiFetched = true;
      state.activeNotifCount = action.payload.activeCount;
    },
    setSustainTeamMembersLoading: (state, action: PayloadAction<boolean | undefined>) => {
      state.sustainTeamMembersLoading = action.payload ?? true;
      if (state.sustainTeamMembersLoading) {
        state.sustainTeamMembersError = null;
      }
    },
    setSustainTeamMembers: (
      state,
      action: PayloadAction<{ data: SustainTeamApiMember[]; totalCount: number }>
    ) => {
      state.sustainTeamMembers = action.payload.data;
      state.sustainTeamMembersTotalCount = action.payload.totalCount;
      state.teamMemberCount = action.payload.totalCount;
      state.sustainTeamMembersLoading = false;
      state.sustainTeamMembersError = null;
    },
    setAllLocations: (state, action: PayloadAction<LocationItem[]>) => {
      state.allLocations = action.payload;
    },
    setSustainTeamMembersError: (state, action: PayloadAction<string>) => {
      state.sustainTeamMembersError = action.payload;
      state.sustainTeamMembersLoading = false;
    },
    /** Replaces the list of active homepage-banner notifications (called from NotificationsCommsScreen). */
    setHomepageBanners: (state, action: PayloadAction<HomepageBannerNotif[]>) => {
      state.homepageBannerNotifs = action.payload;
    },
    /** Marks a banner as dismissed for this session (close icon). */
    dismissHomepageBanner: (state, action: PayloadAction<string>) => {
      if (!state.dismissedHomepageBannerIds.includes(action.payload)) {
        state.dismissedHomepageBannerIds.push(action.payload);
      }
    },
    /**
     * Adds a user-targeted in-app notification (e.g. "Waiting on User" transition).
     * Also records the ticket ID so we don't fire the same notification twice.
     */
    addUserTicketNotif: (state, action: PayloadAction<UserTicketNotif>) => {
      state.userTicketNotifs.unshift(action.payload);
      if (!state.notifiedWaitingTicketIds.includes(action.payload.ticketId)) {
        state.notifiedWaitingTicketIds.push(action.payload.ticketId);
      }
    },
    /** Removes a single user ticket notification (dismiss/close). */
    dismissUserTicketNotif: (state, action: PayloadAction<string>) => {
      state.userTicketNotifs = state.userTicketNotifs.filter((n) => n.id !== action.payload);
    },
    /** Marks a single user ticket notification as read. */
    markUserTicketNotifRead: (state, action: PayloadAction<string>) => {
      const notif = state.userTicketNotifs.find((n) => n.id === action.payload);
      if (notif) notif.read = true;
    },
    /** Marks ALL user ticket notifications as read. */
    markAllUserTicketNotifsRead: (state) => {
      state.userTicketNotifs.forEach((n) => { n.read = true; });
    },
    resetSubmitTicketDraft: (state) => {
      state.submitTicketDraft = createInitialSubmitTicketDraft();
      state.submitTicketTriggerValues = {};
      state.submitTicketStatusMessage = null;
      state.submitTicketStatusTone = null;
      state.repairReplaceReturnEquipmentLines = [];
    },
    resetSubmitTicketState: (state) => {
      state.submitTicketQuery = "";
      state.submitTicketRelatedIssues = [];
      state.submitTicketAttachedIssueIds = []; // GAP-09: no mock seed
      state.submitTicketLoading = false;
      state.submitTicketError = null;
      state.submitTicketStatusMessage = null;
      state.submitTicketStatusTone = null;
      state.submitTicketDraft = createInitialSubmitTicketDraft();
      state.submitTicketTriggerValues = {};
      state.submitTicketLockedFields = {};
      state.repairReplaceReturnEquipmentLines = [];
    },

    setRepairReplaceReturnEquipmentCatalogLoading: (state, action: PayloadAction<boolean>) => {
      state.repairReplaceReturnEquipmentCatalogLoading = action.payload;
    },
    setRepairReplaceReturnEquipmentCatalogSuccess: (state, action: PayloadAction<EquipmentCatalogItem[]>) => {
      state.repairReplaceReturnEquipmentCatalog = action.payload;
      action.payload.forEach((item) => {
        state.repairReplaceReturnEquipmentKnownItems[item.sku] = item;
      });
      state.repairReplaceReturnEquipmentCatalogLoading = false;
    },
    insertRepairReplaceReturnEquipmentLineAfter: (
      state,
      action: PayloadAction<{ afterId: string; sku: string; withUnit: boolean }>
    ) => {
      const lines = state.repairReplaceReturnEquipmentLines;
      const index = lines.findIndex((item) => item.id === action.payload.afterId);
      const newLine = {
        id: `eq-${Date.now()}-${lines.length}`,
        sku: action.payload.sku,
        units: action.payload.withUnit
          ? [{ id: `unit-${Date.now()}-0-0`, serial: "", condition: "failed" as const, serialUnreadable: false }]
          : [],
        quantityGood: "",
        quantityBad: "",
        damageNotes: "",
        failureNotes: "",
        wesContactTime: "",
        wesContactTz: "CT",
      };
      lines.splice(index >= 0 ? index + 1 : lines.length, 0, newLine);
    },
    /**
     * Barcode scan: adds one unit for a SKU, creating the line when the SKU is not on the request yet.
     * Serialized items get the scanned serial (or a blank unit when the code carries none, reusing an
     * existing blank one); quantity items get +1 good quantity.
     */
    addRepairReplaceReturnEquipmentScan: (
      state,
      action: PayloadAction<{ sku: string; capture: "serial" | "quantity"; serial: string; condition: EquipmentUnitCondition }>
    ) => {
      const lines = state.repairReplaceReturnEquipmentLines;
      const { sku, capture, serial, condition } = action.payload;
      let line = lines.find((item) => item.sku === sku);
      if (!line) {
        lines.push({
          id: `eq-${Date.now()}-${lines.length}`,
          sku,
          units: [],
          quantityGood: "",
          quantityBad: "",
          damageNotes: "",
          failureNotes: "",
          wesContactTime: "",
          wesContactTz: "CT",
        });
        line = lines[lines.length - 1];
      }
      if (capture === "quantity") {
        line.quantityGood = String((Number(line.quantityGood) || 0) + 1);
        return;
      }
      const blank = line.units.find((unit) => !unit.serial.trim() && !unit.serialUnreadable);
      if (blank) {
        blank.serial = serial;
        blank.condition = condition;
      } else {
        line.units.push({
          id: `unit-${Date.now()}-${line.units.length}`,
          serial,
          condition,
          serialUnreadable: false,
        });
      }
    },
    addRepairReplaceReturnEquipmentLine: (state, action: PayloadAction<string | undefined>) => {
      state.repairReplaceReturnEquipmentLines.push({
        id: `eq-${Date.now()}-${state.repairReplaceReturnEquipmentLines.length}`,
        sku: action.payload ?? "",
        units: [],
        quantityGood: "",
        quantityBad: "",
        damageNotes: "",
        failureNotes: "",
        wesContactTime: "",
        wesContactTz: "CT",
      });
    },
    /**
     * Bulk Paste: adds pasted serials to the line for each SKU (creating the line when the SKU is not
     * on the request yet). Blank placeholder units on an existing line are replaced by the pasted ones.
     */
    addRepairReplaceReturnEquipmentSerials: (
      state,
      action: PayloadAction<{ groups: Array<{ sku: string; serials: string[]; condition: EquipmentUnitCondition }> }>
    ) => {
      const lines = state.repairReplaceReturnEquipmentLines;
      let counter = 0;
      action.payload.groups.forEach((group) => {
        if (group.serials.length === 0) return;
        let line = lines.find((item) => item.sku === group.sku);
        if (!line) {
          lines.push({
            id: `eq-${Date.now()}-${lines.length}-${counter++}`,
            sku: group.sku,
            units: [],
            quantityGood: "",
            quantityBad: "",
            damageNotes: "",
            failureNotes: "",
            wesContactTime: "",
            wesContactTz: "CT",
          });
          line = lines[lines.length - 1];
        }
        line.units = line.units.filter((unit) => unit.serial.trim() || unit.serialUnreadable);
        group.serials.forEach((serial) => {
          line!.units.push({
            id: `unit-${Date.now()}-${line!.units.length}-${counter++}`,
            serial,
            condition: group.condition,
            serialUnreadable: false,
          });
        });
      });
    },
    addRepairReplaceReturnEquipmentUnits: (
      state,
      action: PayloadAction<{ lineId: string; count: number }>
    ) => {
      const line = state.repairReplaceReturnEquipmentLines.find((item) => item.id === action.payload.lineId);
      if (!line) return;
      for (let i = 0; i < action.payload.count; i++) {
        line.units.push({
          id: `unit-${Date.now()}-${line.units.length}-${i}`,
          serial: "",
          condition: "failed",
          serialUnreadable: false,
        });
      }
    },
    updateRepairReplaceReturnEquipmentUnit: (
      state,
      action: PayloadAction<{
        lineId: string;
        unitId: string;
        patch: Partial<Omit<RepairReplaceReturnEquipmentUnit, "id">>;
      }>
    ) => {
      const line = state.repairReplaceReturnEquipmentLines.find((item) => item.id === action.payload.lineId);
      const unit = line?.units.find((item) => item.id === action.payload.unitId);
      if (unit) Object.assign(unit, action.payload.patch);
    },
    removeRepairReplaceReturnEquipmentUnit: (
      state,
      action: PayloadAction<{ lineId: string; unitId: string }>
    ) => {
      const line = state.repairReplaceReturnEquipmentLines.find((item) => item.id === action.payload.lineId);
      if (line) line.units = line.units.filter((item) => item.id !== action.payload.unitId);
    },
    updateRepairReplaceReturnEquipmentLine: (
      state,
      action: PayloadAction<{ id: string; patch: Partial<Omit<RepairReplaceReturnEquipmentLine, "id">> }>
    ) => {
      const line = state.repairReplaceReturnEquipmentLines.find((item) => item.id === action.payload.id);
      if (line) Object.assign(line, action.payload.patch);
    },
    removeRepairReplaceReturnEquipmentLine: (state, action: PayloadAction<string>) => {
      state.repairReplaceReturnEquipmentLines = state.repairReplaceReturnEquipmentLines.filter(
        (item) => item.id !== action.payload
      );
    },
    removeAllRepairReplaceReturnEquipmentLines: (state) => {
      state.repairReplaceReturnEquipmentLines = [];
    },

    setSubmitTicketLockedFields: (state, action: PayloadAction<SubmitTicketLockedFields>) => {
      state.submitTicketLockedFields = { ...(state.submitTicketLockedFields || {}), ...(action.payload || {}) };
    },
    clearSubmitTicketLockedFields: (state) => {
      state.submitTicketLockedFields = {};
    },
    /**
     * Pre-populates the Submit Ticket draft from an existing closed ticket so
     * the user can re-submit with all fields pre-filled and editable.
     */
    prePopulateSubmitTicket: (
      state,
      action: PayloadAction<{
        title: string;
        description: string;
        stakeholders: string;
        category: string;
        subCategory: string;
        categoryId?: number;
        subCategoryId?: number;
        dynamicValues?: Array<{ fieldConfigId: number; value: string }>;
        level: TicketLevel;
        levelReference: string;
        urgency: UrgencyLevel;
        impactedPeople: string;
        loadsImpacted: LoadImpact;
        reopenedFromId: string;
        /** Pre-fills the Submitter field (e.g. the signed-in user's email for the locked access-request flow) */
        submitter?: string;
      }>
    ) => {
      const p = action.payload;
      state.submitTicketDraft = {
        ...createInitialSubmitTicketDraft(),
        title: p.title,
        description: p.description,
        stakeholders: p.stakeholders,
        category: p.category,
        subCategory: p.subCategory,
        prefillCategoryId: p.categoryId,
        prefillSubCategoryId: p.subCategoryId,
        reopenedDynamicValues: p.dynamicValues ?? [],
        level: p.level,
        levelReference: p.levelReference,
        urgency: p.urgency,
        impactedPeople: p.impactedPeople,
        loadImpact: p.loadsImpacted,
        attachments: [],
        reopenedFromId: p.reopenedFromId,
        submitter: p.submitter ?? createInitialSubmitTicketDraft().submitter,
      };
      state.submitTicketQuery = "";
      state.submitTicketRelatedIssues = [];
      state.submitTicketAttachedIssueIds = [];
      state.submitTicketStatusMessage = null;
      state.submitTicketStatusTone = null;
    },
    /**
     * Consolidates absorbed tickets into the primary ticket on merge confirmation.
     *
     * Rules applied:
     * - Submitters from absorbed tickets → primary stakeholders (deduped, CSV)
     * - Existing stakeholders from absorbed tickets also merged
     * - Affected locations added to primary (deduped by name, case-insensitive)
     * - Attachments transferred with filename collision handling (numeric suffix)
     * - Activity comments imported in chronological order, prefixed [OriginalID]
     * - Urgency elevated to highest across all tickets
     * - mergeImpactNote set to remind team to review impact fields
     * - Absorbed tickets marked "resolved" in detail + sustainQueue
     */
    consolidateMergedTickets: (
      state,
      action: PayloadAction<{
        primaryTicketId: string;
        absorbedTicketIds: string[];
        nowMs: number;
        /** Display name of the Sustain Team member who performed the merge */
        performedBy: string;
      }>
    ) => {
      const { primaryTicketId, absorbedTicketIds, nowMs, performedBy } = action.payload;

      // Format "MM/DD/YYYY at HH:MM AM/PM" for system activity entries
      const mergeDate = new Date(nowMs);
      const mm = String(mergeDate.getMonth() + 1).padStart(2, "0");
      const dd = String(mergeDate.getDate()).padStart(2, "0");
      const yyyy = mergeDate.getFullYear();
      let hours = mergeDate.getHours();
      const minutes = String(mergeDate.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      const formattedDateTime = `${mm}/${dd}/${yyyy} at ${hours}:${minutes} ${ampm}`;
      const primary = state.ticketDetails[primaryTicketId];
      if (!primary) return;

      const URGENCY_ORDER = ["critical", "high", "medium", "low", "informational"] as const;
      type Urg = (typeof URGENCY_ORDER)[number];

      // Collect all existing stakeholder emails on the primary (deduped set)
      const stakeholderSet = new Set<string>(
        (primary.stakeholders ?? "")
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean)
      );

      // Track existing attachment filenames for collision handling
      const existingFileNames = new Set<string>(
        (primary.attachments ?? []).map((a) => a.fileName)
      );
      const mergedAttachments: AttachmentItem[] = [...(primary.attachments ?? [])];

      // Collect absorbed activities for chronological merge
      const absorbedActivities: ActivityItem[] = [];

      let highestUrgency: Urg = primary.urgency as Urg;

      for (const absorbedId of absorbedTicketIds) {
        const absorbed = state.ticketDetails[absorbedId];
        const absorbedQueue = state.sustainQueue.find((t) => t.id === absorbedId);

        // ── Submitters → stakeholders ──────────────────────────────────────
        const submitter = absorbed?.submittedBy || absorbedQueue?.submittedBy;
        if (submitter) {
          const email = submitter.toLowerCase();
          if (email !== (primary.submittedBy ?? "").toLowerCase()) {
            stakeholderSet.add(email);
          }
        }
        // Absorbed ticket's existing stakeholders
        if (absorbed?.stakeholders) {
          absorbed.stakeholders
            .split(",")
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean)
            .forEach((e) => stakeholderSet.add(e));
        }

        // ── Affected locations ─────────────────────────────────────────────
        const existingLocNames = new Set(
          primary.affectedLocations.map((l) => l.name.toLowerCase())
        );
        const absorbedLocs =
          absorbed?.affectedLocations ??
          (absorbedQueue
            ? [{ id: `loc-${absorbedId}`, name: absorbedQueue.location, sapId: "", time: "Merged" }]
            : []);
        for (const loc of absorbedLocs) {
          if (!existingLocNames.has(loc.name.toLowerCase())) {
            primary.affectedLocations = [
              ...primary.affectedLocations,
              { ...loc, id: `loc-merged-${absorbedId}-${loc.id}` },
            ];
            existingLocNames.add(loc.name.toLowerCase());
          }
        }

        // ── Attachments ────────────────────────────────────────────────────
        const absorbedAttachments: AttachmentItem[] = absorbed?.attachments ?? [];
        for (const att of absorbedAttachments) {
          let finalName = att.fileName;
          if (existingFileNames.has(finalName)) {
            const dotIdx = finalName.lastIndexOf(".");
            const base = dotIdx >= 0 ? finalName.slice(0, dotIdx) : finalName;
            const ext = dotIdx >= 0 ? finalName.slice(dotIdx) : "";
            let counter = 1;
            while (existingFileNames.has(`${base}_${counter}${ext}`)) counter++;
            finalName = `${base}_${counter}${ext}`;
          }
          existingFileNames.add(finalName);
          mergedAttachments.push({ ...att, id: `att-merged-${absorbedId}-${att.id}`, fileName: finalName });
        }

        // ── Activity stream import ─────────────────────────────────────────
        const absorbedActivity: ActivityItem[] = absorbed?.activity ?? [];
        absorbedActivities.push(
          ...absorbedActivity.map((a) => ({
            ...a,
            id: `merged-${absorbedId}-${a.id}`,
            content: `[${absorbedId}] ${a.content}`,
          }))
        );

        // ── Urgency elevation ──────────────────────────────────────────────
        const absorbedUrgency = (absorbed?.urgency ?? absorbedQueue?.urgency ?? "low") as Urg;
        if (URGENCY_ORDER.indexOf(absorbedUrgency) < URGENCY_ORDER.indexOf(highestUrgency)) {
          highestUrgency = absorbedUrgency;
        }

        // ── Mark absorbed ticket closed-merged ────────────────────────────
        if (absorbed) {
          absorbed.status = "closed-merged";
          absorbed.closedAt = new Date(nowMs).toISOString();
          absorbed.lastUpdated = "Just now";
          absorbed.mergedIntoTicketId = primaryTicketId;
          // Add system activity to the absorbed ticket
          const mergeActivity: ActivityItem = {
            id: `sys-merge-${nowMs}-${absorbedId}`,
            authorInitials: "SY",
            authorName: "System",
            role: "Auto",
            time: "Just now",
            content: `Ticket merged into ${primaryTicketId} by ${performedBy} on ${formattedDateTime}.`,
            type: "system",
          };
          absorbed.activity = [mergeActivity, ...(absorbed.activity ?? [])];
        }
        const qIdx = state.sustainQueue.findIndex((t) => t.id === absorbedId);
        if (qIdx >= 0) {
          state.sustainQueue[qIdx] = {
            ...state.sustainQueue[qIdx],
            status: "closed-merged",
            lastUpdated: "Just now",
            mergedIntoTicketId: primaryTicketId,
            hasDuplicate: false,
          };
        }
        const myIdx = state.myTickets.findIndex((t) => t.id === absorbedId);
        if (myIdx >= 0) {
          state.myTickets[myIdx] = {
            ...state.myTickets[myIdx],
            status: "closed-merged",
            lastUpdated: "Just now",
            mergedIntoTicketId: primaryTicketId,
            hasDuplicate: false,
          };
        }
      }

      // ── Apply merged stakeholders ────────────────────────────────────────
      stakeholderSet.delete((primary.submittedBy ?? "").toLowerCase());
      primary.stakeholders = [...stakeholderSet].join(", ");

      // ── Apply merged attachments ─────────────────────────────────────────
      primary.attachments = mergedAttachments;

      // ── Merge absorbed activities in chronological order ─────────────────
      primary.activity = [...absorbedActivities, ...(primary.activity ?? [])];

      // ── Apply highest urgency ────────────────────────────────────────────
      primary.urgency = highestUrgency;
      const primaryQueueIdx = state.sustainQueue.findIndex((t) => t.id === primaryTicketId);
      if (primaryQueueIdx >= 0) {
        state.sustainQueue[primaryQueueIdx] = {
          ...state.sustainQueue[primaryQueueIdx],
          urgency: highestUrgency,
          hasDuplicate: false,
        };
      }
      // Also clear from ticket details
      if (primary) {
        primary.hasDuplicate = false;
      }

      // ── Impact note ──────────────────────────────────────────────────────
      primary.mergeImpactNote =
        "Additional impact from merged tickets — review and update as needed.";

      // ── Track absorbed IDs ───────────────────────────────────────────────
      primary.absorbedTicketIds = [
        ...(primary.absorbedTicketIds ?? []),
        ...absorbedTicketIds,
      ];

      // ── Primary ticket system activity ───────────────────────────────────
      const totalLocations = primary.affectedLocations.length;
      const idList = absorbedTicketIds.join(", ");
      const primaryActivity: ActivityItem = {
        id: `sys-merge-primary-${nowMs}`,
        authorInitials: "SY",
        authorName: "System",
        role: "Auto",
        time: "Just now",
        content: `Absorbed ${absorbedTicketIds.length} duplicate ticket${absorbedTicketIds.length > 1 ? "s" : ""}: ${idList}. Impact consolidated from ${totalLocations} total location${totalLocations !== 1 ? "s" : ""}.`,
        type: "system",
      };
      primary.activity = [primaryActivity, ...(primary.activity ?? [])];

      primary.lastUpdated = "Just now";

      // ── Notifications ────────────────────────────────────────────────────
      const isoNow = new Date(nowMs).toISOString();

      // One notification per absorbed ticket — goes to the submitter/stakeholders of that ticket
      for (const absorbedId of absorbedTicketIds) {
        const absorbedNotif: UserTicketNotif = {
          id: `notif-merge-absorbed-${nowMs}-${absorbedId}`,
          ticketId: absorbedId,
          primaryTicketId,
          message: `Your ticket ${absorbedId} has been merged with related reports. Track resolution progress at ${primaryTicketId}.`,
          createdAt: isoNow,
          read: false,
          channels: ["in-app", "email", "teams"],
        };
        state.userTicketNotifs.unshift(absorbedNotif);
      }

      // One notification for the Sustain Team member assigned to the primary ticket
      const totalSubmitters = stakeholderSet.size + 1; // +1 for primary submitter
      const sustainNotif: UserTicketNotif = {
        id: `notif-merge-sustain-${nowMs}`,
        ticketId: primaryTicketId,
        message: `${absorbedTicketIds.length} ticket${absorbedTicketIds.length > 1 ? "s" : ""} merged into ${primaryTicketId}. Impact now includes ${primary.affectedLocations.length} location${primary.affectedLocations.length !== 1 ? "s" : ""} and ${totalSubmitters} total submitter${totalSubmitters !== 1 ? "s" : ""}.`,
        createdAt: isoNow,
        read: false,
        channels: ["in-app"],
      };
      state.userTicketNotifs.unshift(sustainNotif);
    },
    /**
     * Transitions a sustain queue ticket to a new status and updates both
     * the Active Resolution Timer and the Waiting on IT Timer accordingly.
     *
     * Call with a `nowMs` timestamp (Date.now()) so the reducer stays pure
     * and deterministic.
     */
    /**
     * Saves the current Sustain Queue list state to Redux just before navigating
     * to Ticket Detail. Pass null to explicitly clear (fresh navigation).
     */
    saveSustainQueueListState: (state, action: PayloadAction<SustainQueueListState | null>) => {
      state.sustainQueueListState = action.payload;
    },
    /**
     * Saves the current My Tickets list state to Redux just before navigating
     * to Ticket Detail. Pass null to explicitly clear (fresh navigation).
     */
    saveMyTicketsListState: (state, action: PayloadAction<MyTicketsListState | null>) => {
      state.myTicketsListState = action.payload;
    },
    transitionSustainTicketStatus: (
      state,
      action: PayloadAction<{ ticketId: string; nextStatus: TicketStatus; nowMs: number }>
    ) => {
      const { ticketId, nextStatus, nowMs } = action.payload;
      const idx = state.sustainQueue.findIndex((t) => t.id === ticketId);
      if (idx === -1) return;

      const ticket = state.sustainQueue[idx];
      const prevStatus = ticket.status;
      if (prevStatus === nextStatus) return;

      const enteredAt = ticket.statusEnteredAt ?? nowMs;
      const elapsed = nowMs - enteredAt;

      // Close out the current history entry
      const history: StatusHistoryEntry[] = ticket.statusHistory
        ? ticket.statusHistory.map((e) => ({ ...e }))
        : [];
      if (history.length > 0 && !history[history.length - 1].exitedAt) {
        history[history.length - 1].exitedAt = nowMs;
      }
      // Open a new history entry
      history.push({ status: nextStatus, enteredAt: nowMs });

      // Accumulate timers
      const waitingItMs = (ticket.waitingItMs ?? 0) +
        (prevStatus === "waiting-it" ? elapsed : 0);
      const activeResolutionMs = (ticket.activeResolutionMs ?? 0) +
        (prevStatus !== "waiting-it" ? elapsed : 0);

      state.sustainQueue[idx] = {
        ...ticket,
        status: nextStatus,
        statusEnteredAt: nowMs,
        statusHistory: history,
        waitingItMs,
        waitingItMinutes: Math.round(waitingItMs / 60000),
        activeResolutionMs,
        minutesAging: Math.round(activeResolutionMs / 60000),
        lastUpdated: "Just now",
      };
    },
  },
});

export const {
  setLoading,
  setData,
  setError,
  setMyTicketsLoading,
  setMyTickets,
  setMyTicketsTotalElements,
  setClosedTicketsCount,
  setMyTicketsError,
  setSustainQueueLoading,
  setSustainQueue,
  setSustainQueueTotalElements,
  setSustainQueueCurrentPage,
  setSelectedTicketId,
  setTicketDetailSource,
  setDetailLoading,
  setPendingAccessRequestTicketId,
  upsertTicketDetail,
  updateTicketDetailInfo,
  addTicketActivityComment,
  addTicketNote,
  setTicketNotes,
  addAffectedLocation,
  removeAffectedLocation,
  addTicketStakeholderEmail,
  updateAffectedLocations,
  updateTicketStatus,
  transitionSustainTicketStatus,
  consolidateMergedTickets,
  setTriageInfo,
  setSubmitTicketQuery,
  setSubmitTicketFormConfigLoading,
  setSubmitTicketFormConfigSuccess,
  setSubmitTicketDynamicFields,
  setSubmitTicketTriggerFieldOptions,
  setSubmitTicketTriggerFieldValue,
  clearSubmitTicketTriggerValues,
  setSubmitTicketFormConfigError,
  setSubmitTicketLoading,
  setSubmitTicketRelatedIssues,
  setSubmitTicketError,
  setSubmitTicketSubmitter,
  setSubmitTicketTitle,
  setSubmitTicketDescription,
  setSubmitTicketStakeholders,
  setSubmitTicketCategory,
  setSubmitTicketSubCategory,
  setSubmitTicketTertiaryCategory,
  setSubmitTicketLevel,
  setSubmitTicketLevelReference,
  setSubmitTicketSapId,
  setSubmitTicketUrgency,
  setSubmitTicketImpactedPeople,
  setSubmitTicketLoadImpact,
  setSubmitTicketLockedFields,
  clearSubmitTicketLockedFields,
  attachSubmitTicketToIssue,
  addSubmitTicketAttachment,
  removeSubmitTicketAttachment,
  setSubmitTicketMmsToggle,
  setSubmitTicketStatusMessage,
  resetSubmitTicketDraft,
  resetSubmitTicketState,
  setRepairReplaceReturnEquipmentCatalogLoading,
  setRepairReplaceReturnEquipmentCatalogSuccess,
  addRepairReplaceReturnEquipmentLine,
  insertRepairReplaceReturnEquipmentLineAfter,
  addRepairReplaceReturnEquipmentSerials,
  addRepairReplaceReturnEquipmentScan,
  addRepairReplaceReturnEquipmentUnits,
  updateRepairReplaceReturnEquipmentUnit,
  removeRepairReplaceReturnEquipmentUnit,
  updateRepairReplaceReturnEquipmentLine,
  removeRepairReplaceReturnEquipmentLine,
  removeAllRepairReplaceReturnEquipmentLines,
  prePopulateSubmitTicket,
  setActiveNotifCount,
  setTeamMemberCount,
  setTicketingTeams,
  setAvailableTicketingTeams,
  setHomepageBanners,
  dismissHomepageBanner,
  addUserTicketNotif,
  dismissUserTicketNotif,
  markUserTicketNotifRead,
  markAllUserTicketNotifsRead,
  setDashboardCountsLoading,
  setDashboardCounts,
  setSustainTeamMembersLoading,
  setSustainTeamMembers,
  setSustainTeamMembersError,
  setNotificationsApiCache,
  setAllLocations,
  saveSustainQueueListState,
  saveMyTicketsListState,
} = ticketingPortalSlice.actions;
export default ticketingPortalSlice.reducer;
